import { useRef, useState } from "react";
import { api, API, fmt, fmtPct } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const PRESETS = [
  { label: "AAPL — Apple long thesis", q: "AAPL 12-month outlook and thesis", s: "AAPL" },
  { label: "NVDA — AI supercycle", q: "NVDA valuation & AI capex sustainability", s: "NVDA" },
  { label: "BTC — macro regime", q: "Bitcoin BTC-USD macro regime & institutional flows", s: "BTC-USD" },
  { label: "RELIANCE.NS", q: "Reliance Industries India cross-cycle analysis", s: "RELIANCE.NS" },
  { label: "GOLD", q: "Gold outlook amid rates & geopolitics", s: "GC=F" },
];

export default function Research() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [symbol, setSymbol] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [attachments, setAttachments] = useState([]); // [{id, original_filename, size}]
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileRef = useRef(null);

  const onAttach = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!user) { toast.error("Sign in to attach documents"); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10 MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("purpose", "research_doc");
    setUploading(true);
    try {
      const { data } = await api.post("/files/upload", fd);
      setAttachments((prev) => [...prev, data]);
      toast.success(`Attached: ${data.original_filename}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removeAttachment = (id) => setAttachments((p) => p.filter((a) => a.id !== id));

  const run = async (q = query, s = symbol) => {
    if (!q.trim()) { toast.error("Enter a research query"); return; }
    setLoading(true); setReport(null);
    try {
      const { data } = await api.post("/ai/research", {
        query: q, symbol: s || null,
        attached_file_ids: attachments.map((a) => a.id),
      });
      setReport(data);
      toast.success(`Report generated · Confidence ${data.confidence_score}%`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Research generation failed");
    } finally { setLoading(false); }
  };

  const exportPdf = async () => {
    if (!user) { toast.error("Sign in to export PDF"); return; }
    if (!report?._id) { toast.error("No report to export"); return; }
    setExporting(true);
    try {
      const { data } = await api.post(`/ai/research/${report._id}/export-pdf`);
      const backend = API.replace(/\/api$/, "");
      // fetch as blob (auth cookies included) and trigger download
      const resp = await api.get(data.url, { responseType: "blob", baseURL: backend });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = data.filename || "research.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("PDF exported");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally { setExporting(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="research-page">
      <div className="border-b border-[color:var(--border-default)] pb-4 mb-6">
        <div className="text-[10px] font-mono-data text-[#FFD700] tracking-widest mb-2">◆ MULTI-AGENT AI RESEARCH</div>
        <h1 className="font-serif-elite text-4xl md:text-5xl font-bold tracking-tight">Deep Research Engine</h1>
        <p className="text-[color:var(--text-secondary)] text-sm mt-2 max-w-2xl">
          Query any equity, index, commodity, forex pair, or crypto asset. The system synthesizes macro, fundamental,
          technical, quant, and smart-money perspectives into a probability-weighted institutional report.
        </p>
      </div>

      {/* Query input */}
      <div className="border border-[color:var(--border-default)] p-4 bg-[color:var(--bg-elevated)] mb-6" data-testid="query-panel">
        <div className="grid grid-cols-1 md:grid-cols-[1fr,180px,140px] gap-2">
          <input
            data-testid="research-query-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && run()}
            placeholder="e.g. Nvidia 12-month outlook & AI capex sustainability"
            className="bg-[color:var(--bg-surface)] border border-[color:var(--border-default)] px-3 py-2.5 text-sm text-white placeholder:text-[color:var(--text-muted)] focus:outline-none focus:border-[color:var(--border-focus)] font-body"
          />
          <input
            data-testid="research-symbol-input"
            value={symbol}
            onChange={e => setSymbol(e.target.value.toUpperCase())}
            placeholder="Ticker (e.g. NVDA)"
            className="bg-[color:var(--bg-surface)] border border-[color:var(--border-default)] px-3 py-2.5 text-sm text-white placeholder:text-[color:var(--text-muted)] focus:outline-none focus:border-[color:var(--border-focus)] font-mono-data"
          />
          <button
            data-testid="run-research-btn"
            onClick={() => run()}
            disabled={loading}
            className="bg-white text-black px-4 py-2.5 text-xs font-mono-data tracking-widest hover:opacity-90 disabled:opacity-50 transition-colors"
          >
            {loading ? "SYNTHESIZING…" : "RUN ANALYSIS →"}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {PRESETS.map(p => (
            <button
              key={p.label}
              data-testid={`preset-${p.s}`}
              onClick={() => { setQuery(p.q); setSymbol(p.s); run(p.q, p.s); }}
              className="text-[10px] font-mono-data px-2 py-1 border border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)] hover:text-white transition-colors"
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Attach documents */}
        <div className="mt-3 border-t border-[color:var(--border-default)] pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">
              ATTACH DOCUMENTS <span className="text-[#52525B]">(PDF · CSV · TXT · MD — max 10 MB, 5 files)</span>
            </div>
            <button
              data-testid="attach-doc-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || !user || attachments.length >= 5}
              className="text-[10px] font-mono-data px-2 py-1 border border-[color:var(--border-default)] text-white hover:bg-[color:var(--bg-hover)] disabled:opacity-40 transition-colors"
              title={!user ? "Sign in to attach documents" : ""}
            >
              {uploading ? "UPLOADING…" : "+ ATTACH FILE"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.csv,.txt,.md,application/pdf,text/csv,text/plain"
              onChange={onAttach}
              className="hidden"
              data-testid="research-file-input"
            />
          </div>
          {attachments.length === 0 ? (
            <div className="text-[10px] font-body text-[#52525B]">
              {user ? "No attachments. Add earnings PDFs, CSVs, or notes to inject into the AI context." : "Sign in to attach documents to your research."}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {attachments.map((a) => (
                <div key={a.id} data-testid={`attachment-${a.id}`} className="flex items-center gap-2 border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] px-2 py-1">
                  <span className="text-[10px] font-mono-data text-white truncate max-w-[220px]">{a.original_filename}</span>
                  <span className="text-[10px] font-mono-data text-[color:var(--text-muted)]">{Math.round(a.size / 1024)}KB</span>
                  <button
                    data-testid={`remove-attachment-${a.id}`}
                    onClick={() => removeAttachment(a.id)}
                    className="text-[10px] text-[color:var(--text-muted)] hover:text-bear"
                  >×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading && <ResearchSkeleton />}
      {report && <Report r={report} onExport={exportPdf} exporting={exporting} canExport={!!user} />}
      {!loading && !report && (
        <div className="border border-dashed border-[color:var(--border-default)] p-16 text-center" data-testid="empty-state">
          <div className="font-serif-elite text-2xl text-[color:var(--text-secondary)]">Awaiting query.</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-2 font-mono-data">Select a preset above or enter a custom research prompt.</div>
        </div>
      )}
    </div>
  );
}

function ResearchSkeleton() {
  return (
    <div className="space-y-3" data-testid="research-loading">
      <div className="h-32 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)]" />
      <div className="grid grid-cols-3 gap-3">
        <div className="h-40 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)]" />
        <div className="h-40 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)]" />
        <div className="h-40 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)]" />
      </div>
      <div className="text-xs font-mono-data text-[color:var(--text-muted)] text-center">Multi-agent synthesis in progress · macro · quant · fundamental · technical · smart-money</div>
    </div>
  );
}

function Report({ r, onExport, exporting, canExport }) {
  const recColor = r.recommendation?.includes("BUY") ? "text-bull" : r.recommendation?.includes("SELL") ? "text-bear" : "text-[#8E8E93]";
  return (
    <div className="space-y-4" data-testid="research-report">
      {r._demo && (
        <div className="border border-[#FFD700]/50 bg-[#FFD700]/5 px-4 py-2 text-[11px] font-mono-data text-gold" data-testid="demo-badge">
          ◆ DEMO OUTPUT — LLM took longer than 50s edge cap; showing deterministic fallback. Retry for full multi-agent synthesis.
        </div>
      )}
      {r._attachments?.length > 0 && (
        <div className="border border-[color:var(--border-default)] px-4 py-2 text-[11px] font-mono-data text-[color:var(--text-secondary)]" data-testid="attachments-used">
          ◆ INJECTED CONTEXT: {r._attachments.map(a => a.filename).join(" · ")}
        </div>
      )}
      {/* Header card */}
      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono-data text-lg text-white">{r.ticker}</span>
              <span className="text-[10px] font-mono-data text-[color:var(--text-muted)] border border-[color:var(--border-default)] px-1.5">{r.asset_class}</span>
              <button
                data-testid="export-pdf-btn"
                onClick={onExport}
                disabled={exporting || !canExport}
                title={!canExport ? "Sign in to export PDF" : ""}
                className="ml-auto text-[10px] font-mono-data tracking-widest border border-[color:var(--border-default)] px-2 py-1 text-white hover:bg-[color:var(--bg-hover)] disabled:opacity-40 transition-colors"
              >
                {exporting ? "EXPORTING…" : "↓ EXPORT PDF"}
              </button>
            </div>
            <p className="font-serif-elite text-xl md:text-2xl leading-snug tracking-tight text-white" data-testid="executive-summary">
              {r.executive_summary}
            </p>
          </div>
          <div className="flex gap-4 items-start">
            <Stat label="RECOMMENDATION" value={r.recommendation} valueClass={`${recColor} font-mono-data`} />
            <Stat label="CONFIDENCE" value={`${r.confidence_score}%`} valueClass="text-gold font-mono-data" />
            <Stat label="12M EXP. RETURN" value={fmtPct(r.expected_return_12m_pct)} valueClass={`${r.expected_return_12m_pct >= 0 ? "text-bull" : "text-bear"} font-mono-data`} />
          </div>
        </div>
      </div>

      {/* Scenario cases */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[color:var(--border-default)]">
        <ScenarioCard c={r.bull_case} label="BULL CASE" tone="bull" />
        <ScenarioCard c={r.base_case} label="BASE CASE" tone="neutral" />
        <ScenarioCard c={r.bear_case} label="BEAR CASE" tone="bear" />
      </div>

      {/* Catalysts & Risks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[color:var(--border-default)]">
        <ListBlock title="KEY CATALYSTS" items={r.key_catalysts} tone="bull" />
        <ListBlock title="KEY RISKS" items={r.key_risks} tone="bear" />
      </div>

      {/* Narratives */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[color:var(--border-default)]">
        <TextBlock title="INSTITUTIONAL POSITIONING" body={r.institutional_positioning} extra={`Smart money: ${r.smart_money_signal}`} />
        <TextBlock title="VALUATION" body={r.valuation_summary} />
        <TextBlock title="TECHNICAL SUMMARY" body={r.technical_summary} />
        <TextBlock title="MACRO CONTEXT" body={r.macro_context} />
      </div>
    </div>
  );
}

function Stat({ label, value, valueClass }) {
  return (
    <div>
      <div className="text-[9px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
      <div className={`text-lg mt-1 ${valueClass || "text-white"}`}>{value}</div>
    </div>
  );
}

function ScenarioCard({ c, label, tone }) {
  const color = tone === "bull" ? "text-bull" : tone === "bear" ? "text-bear" : "text-[color:var(--text-secondary)]";
  return (
    <div className="p-4 border-r border-[color:var(--border-default)] last:border-r-0" data-testid={`scenario-${tone}`}>
      <div className={`text-[10px] font-mono-data tracking-widest ${color} mb-2`}>{label} · {c?.probability_pct}%</div>
      <div className="font-mono-data text-2xl text-white mb-1">{fmt(c?.target)}</div>
      <p className="text-xs text-[color:var(--text-secondary)] leading-relaxed mb-2 font-body">{c?.thesis}</p>
      {(c?.catalysts || c?.risks) && (
        <ul className="space-y-0.5 mt-2">
          {(c.catalysts || c.risks || []).map((x) => (
            <li key={x} className="text-[11px] text-[color:var(--text-muted)] font-body">→ {x}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ListBlock({ title, items, tone }) {
  const color = tone === "bull" ? "text-bull" : "text-bear";
  return (
    <div className="border-r border-b border-[color:var(--border-default)] p-4 last:border-r-0">
      <div className={`text-[10px] font-mono-data tracking-widest ${color} mb-3`}>{title}</div>
      <ul className="space-y-2">
        {(items || []).map((it, i) => (
          <li key={`${title}-${it}-${i}`} className="text-sm text-white font-body flex gap-2">
            <span className={`${color} font-mono-data`}>0{i + 1}</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TextBlock({ title, body, extra }) {
  return (
    <div className="border-r border-b border-[color:var(--border-default)] p-4">
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-secondary)] mb-2">{title}</div>
      <p className="text-sm text-white font-body leading-relaxed">{body}</p>
      {extra && <div className="text-[11px] font-mono-data text-gold mt-2">{extra}</div>}
    </div>
  );
}
